import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

export type CoreKey = "problem" | "who" | "dish" | "ingredients" | "today" | "good";
export type SectionState = "empty" | "draft" | "not_sure" | "done";
export type AdminLabel = "Noted" | "In discussion" | "In build" | "Live" | "Parked";

export interface Assessment {
  status: "clear" | "needs_more" | "unassessed";
  missing: string[];
  note: string;
  model: string;
  promptVersion: string;
  contentHash: string;
  assessedAt: string;
}

export interface Section {
  id: string;
  coreKey: CoreKey | null;
  star: boolean;
  title: string;
  guidingQuestion: string | null;
  content: string;
  state: SectionState;
  assessment: Assessment | null;
  assessmentStale: boolean;
  sortOrder: number;
  updatedBy: "user" | "ai";
  updatedAt: string;
}

export interface Attachment {
  id: string;
  sectionId: string | null;
  filename: string;
  mime: string;
  size: number;
  rowCount: number | null;
  hasText: boolean;
  aiSummary: string | null;
  createdAt: string;
}

export interface VersionMeta {
  id: string;
  version: number;
  submittedAt: string;
  notifiedAt: string | null;
  /** Files the version lists; "All files" downloads them with the PDF as a zip. */
  fileCount: number;
}

export interface BriefListItem {
  id: string;
  title: string;
  summary: string | null;
  submissionCount: number;
  lastSubmittedAt: string | null;
  editedSinceSubmit: boolean;
  adminLabel: AdminLabel | null;
  ownerUnseen: boolean;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  clear: number;
  core: number;
}

export interface Brief {
  id: string;
  ownerId: string;
  title: string;
  summary: string | null;
  startFrom: "dish" | "ingredients" | null;
  aiTier: "fast" | "detail";
  submissionCount: number;
  lastSubmittedAt: string | null;
  editedSinceSubmit: boolean;
  adminLabel: AdminLabel | null;
  adminRemark: string | null;
  adminAt: string | null;
  ownerUnseen: boolean;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  clear: number;
  core: number;
}

export interface Access {
  as: "owner" | "admin" | "superadmin";
  canEdit: boolean;
  canDelete: boolean;
  canLabel: boolean;
  canReadTranscript: boolean;
}

export interface BriefDetail {
  viewing: "live" | "version";
  version?: number;
  brief: Brief;
  sections: Section[];
  attachments: Attachment[];
  versions: VersionMeta[];
  access: Access;
}

export interface Revision {
  id: string;
  content: string;
  author: "user" | "ai";
  outcome: "kept" | "undone" | "edited" | null;
  createdAt: string;
}

export const briefKey = (id: string) => ["brief", id] as const;

export function useBriefs(scope: "active" | "archived") {
  return useQuery({
    queryKey: ["briefs", scope],
    queryFn: () => api<{ briefs: BriefListItem[] }>(`/briefs?scope=${scope}`).then((r) => r.briefs),
  });
}

export function useBrief(id: string) {
  return useQuery({
    queryKey: briefKey(id),
    queryFn: () => api<BriefDetail>(`/briefs/${id}`),
    staleTime: 10_000,
  });
}

/** Everything that changes a brief, each one keeping the cached detail in step. */
export function useBriefActions(id: string) {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: briefKey(id) });
    void qc.invalidateQueries({ queryKey: ["briefs"] });
  };
  const patchSectionInCache = (section: Section) => {
    qc.setQueryData<BriefDetail>(briefKey(id), (d) =>
      d ? { ...d, sections: d.sections.map((s) => (s.id === section.id ? section : s)) } : d,
    );
  };

  const patchBrief = useMutation({
    mutationFn: (patch: Partial<Pick<Brief, "title" | "summary" | "startFrom" | "aiTier">>) =>
      api<{ brief: Brief }>(`/briefs/${id}`, { method: "PATCH", body: patch }),
    onSuccess: ({ brief }) => {
      qc.setQueryData<BriefDetail>(briefKey(id), (d) => (d ? { ...d, brief: { ...d.brief, ...brief } } : d));
      void qc.invalidateQueries({ queryKey: ["briefs"] });
    },
  });

  const patchSection = useMutation({
    mutationFn: ({
      sectionId,
      ...patch
    }: { sectionId: string } & Partial<{
      content: string;
      state: "draft" | "not_sure" | "done";
      title: string;
      guidingQuestion: string | null;
    }>) =>
      api<{ section: Section }>(`/briefs/${id}/sections/${sectionId}`, { method: "PATCH", body: patch }),
    onSuccess: ({ section }) => {
      patchSectionInCache(section);
      void qc.invalidateQueries({ queryKey: ["briefs"] });
    },
  });

  const addSection = useMutation({
    mutationFn: (body: { title: string; guidingQuestion?: string | null }) =>
      api<{ section: Section }>(`/briefs/${id}/sections`, { method: "POST", body }),
    onSuccess: invalidate,
  });

  const deleteSection = useMutation({
    mutationFn: (sectionId: string) => api(`/briefs/${id}/sections/${sectionId}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });

  const reorder = useMutation({
    mutationFn: (order: string[]) =>
      api<{ sections: Section[] }>(`/briefs/${id}/sections/reorder`, { method: "POST", body: { order } }),
    onSuccess: ({ sections }) => {
      qc.setQueryData<BriefDetail>(briefKey(id), (d) => (d ? { ...d, sections } : d));
    },
  });

  const restoreRevision = useMutation({
    mutationFn: ({ sectionId, revisionId }: { sectionId: string; revisionId: string }) =>
      api<{ section: Section }>(`/briefs/${id}/sections/${sectionId}/revisions/${revisionId}/restore`, {
        method: "POST",
      }),
    onSuccess: ({ section }) => patchSectionInCache(section),
  });

  const keep = useMutation({
    mutationFn: (sectionId: string) =>
      api<{ section: Section }>(`/briefs/${id}/sections/${sectionId}/keep`, { method: "POST" }),
    onSuccess: ({ section }) => patchSectionInCache(section),
  });

  const upload = useMutation({
    mutationFn: ({ file, sectionId }: { file: File; sectionId: string | null }) => {
      const form = new FormData();
      form.append("file", file, file.name);
      if (sectionId) form.append("sectionId", sectionId);
      return api<{ attachment: Attachment }>(`/briefs/${id}/attachments`, { method: "POST", body: form });
    },
    onSuccess: invalidate,
  });

  const deleteAttachment = useMutation({
    mutationFn: (attachmentId: string) => api(`/briefs/${id}/attachments/${attachmentId}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });

  const submit = useMutation({
    mutationFn: () =>
      api<{ version: number; submittedAt: string; pdf: boolean }>(`/briefs/${id}/submit`, { method: "POST" }),
    onSuccess: invalidate,
  });

  const archive = useMutation({
    mutationFn: (archived: boolean) =>
      api(`/briefs/${id}/${archived ? "archive" : "restore"}`, { method: "POST" }),
    onSuccess: invalidate,
  });

  const seen = useMutation({
    mutationFn: () => api(`/briefs/${id}/seen`, { method: "POST" }),
    onSuccess: invalidate,
  });

  return {
    patchBrief,
    patchSection,
    addSection,
    deleteSection,
    reorder,
    restoreRevision,
    keep,
    upload,
    deleteAttachment,
    submit,
    archive,
    seen,
  };
}

export function useRevisions(id: string, sectionId: string | null) {
  return useQuery({
    queryKey: ["revisions", id, sectionId],
    queryFn: () =>
      api<{ revisions: Revision[] }>(`/briefs/${id}/sections/${sectionId}/revisions`).then((r) => r.revisions),
    enabled: sectionId !== null,
  });
}

export function useBriefListActions() {
  const qc = useQueryClient();
  const invalidate = () => void qc.invalidateQueries({ queryKey: ["briefs"] });
  const create = useMutation({
    mutationFn: () => api<{ brief: Brief }>("/briefs", { method: "POST" }),
    onSuccess: invalidate,
  });
  const duplicate = useMutation({
    mutationFn: (id: string) => api<{ brief: Brief }>(`/briefs/${id}/duplicate`, { method: "POST" }),
    onSuccess: invalidate,
  });
  const archive = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      api(`/briefs/${id}/${archived ? "archive" : "restore"}`, { method: "POST" }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/briefs/${id}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });
  return { create, duplicate, archive, remove };
}

export function fmtDate(iso: string | null): string {
  if (!iso) return "";
  return new Intl.DateTimeFormat("en-SG", { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
}

export function fmtSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
